import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Box,
  CollectionPreferences,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from "@amzn/awsui-components-console";
import React, { memo, useState } from "react";

import { DEFAULT_PREFERENCES } from "@/utils/constants";

import styles from "./ChatMarkdownTable.module.css";
import { TablePreferences } from "./types";

export type ChatMarkdownTableProps = Pick<
  TableProps,
  "items" | "columnDefinitions"
> & {
  loading?: boolean;
  wrapLines?: boolean;
  onRowClick?: TableProps["onRowClick"];
  /**
   * When true, columns get drag handles so the user can resize them. Enables
   * width-driven cell truncation (e.g. the filename column). Defaults to false.
   */
  resizableColumns?: boolean;
  /**
   * 1-based index of the column whose resize handle should remain visible.
   * All other columns will have their resize handle hidden and their
   * header/cell ellipsis overridden so content is never clipped.
   * Omit (undefined) to leave all columns resizable (the default behaviour).
   */
  resizableColumnIndex?: number;
  /**
   * Called when the user drags a column resize handle.
   */
  onColumnWidthsChange?: TableProps["onColumnWidthsChange"];
};

const ChatMarkdownTable: React.FC<ChatMarkdownTableProps> = ({
  items: allItems,
  columnDefinitions,
  loading = false,
  wrapLines = true,
  onRowClick,
  resizableColumns = false,
  resizableColumnIndex,
  onColumnWidthsChange,
}) => {
  const [preferences, setPreferences] =
    useState<TablePreferences>(DEFAULT_PREFERENCES);
  const { items, collectionProps, paginationProps } = useCollection(allItems, {
    sorting: {},
    pagination: { pageSize: preferences.pageSize },
  });

  return (
    <Box padding={{ vertical: "xs" }}>
      <div
        className={onRowClick ? styles.clickableRows : undefined}
        data-clickable-rows={onRowClick ? "true" : "false"}
        data-resizable-col={resizableColumnIndex ?? undefined}
      >
        <Table
          {...collectionProps}
          variant="borderless"
          renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
            `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
          }
          header={
            <Header
              counter={`Total Count: (${allItems.length})`}
              actions={
                <>
                  <Pagination {...paginationProps} />
                  <CollectionPreferences
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    preferences={preferences}
                    pageSizePreference={{
                      title: "Page Size",
                      options: [
                        { value: 10, label: "10 items" },
                        { value: 25, label: "25 items" },
                        { value: 50, label: "50 items" },
                      ],
                    }}
                    stripedRowsPreference={{
                      label: "Striped Rows",
                      description: "Select to add alternating shaded rows",
                    }}
                    onConfirm={({ detail }) =>
                      setPreferences(detail as TablePreferences)
                    }
                  />
                </>
              }
              variant="h3"
            />
          }
          columnDefinitions={columnDefinitions}
          items={items}
          loading={loading}
          wrapLines={wrapLines}
          resizableColumns={resizableColumns}
          onColumnWidthsChange={onColumnWidthsChange}
          onRowClick={onRowClick}
          stripedRows={preferences.stripedRows}
          empty={
            <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <b>No items to display.</b>
              </SpaceBetween>
            </Box>
          }
        />
      </div>
    </Box>
  );
};

export default memo(ChatMarkdownTable);
